import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import './css/branches.css';
import FooterNote from './FooterNote';

// Placeholder store image (gray store icon as data URL)
const placeholderStoreImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23e0e0e0' width='200' height='200'/%3E%3Cpath fill='%23b0b0b0' d='M50 50h100v100H50z'/%3E%3Cpath fill='%23999' d='M60 60h80v20H60zm0 30h80v20H60zm0 30h80v20H60z'/%3E%3C/svg%3E";

const Branches = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'display_order'

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`);
      // Filter only active branches
      const activeBranches = response.data.filter(branch => branch.is_active);
      setBranches(activeBranches);
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform branches for display with translations
  const transformedBranches = useMemo(() => {
    return branches.map(branch => ({
      id: branch.id,
      name: language === 'ar' ? branch.name_ar : branch.name_en,
      location: language === 'ar' ? branch.location_ar : branch.location_en,
      phone: branch.phone || '',
          image: branch.image ? getUploadUrl(branch.image) : placeholderStoreImage,
      display_order: branch.display_order
    }));
  }, [branches, language]);

  // Filter and sort branches
  const filteredBranches = useMemo(() => {
    let filtered = [...transformedBranches];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(branch =>
        branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort branches
    if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'display_order') {
      filtered.sort((a, b) => a.display_order - b.display_order);
    }

    return filtered;
  }, [transformedBranches, searchTerm, sortBy]);

  if (loading) {
    return (
      <section className='main-view'>
        <section className='branches'>
          <div className='container-fluid'>
            <div className='loading-message'>
              <p>{t('branches.loading') || 'Loading...'}</p>
            </div>
          </div>
        </section>
        <FooterNote />
      </section>
    );
  }

  return (
    <section className='main-view'>
      <section className='branches'>
        <div className='container-fluid'>
          <div className='branches-content'>
            {/* Header */}
            <div className='branches-header'>
              <button 
                className='back-btn'
                onClick={() => navigate('/')}
              >
                {language === 'ar' ? '← ' : ''}{t('nav.back')}{language === 'en' ? ' ←' : ''}
              </button>
              <h1 className='branches-title'>{t('branches.title')}</h1>
            </div>

            {/* Filter Button */}
            <div className='branches-filter-header'>
              <button 
                className='open-filter-btn'
                onClick={() => setShowFilter(!showFilter)}
              >
                <FunnelIcon className='icon' />
                <span>{t('branches.filter')}</span>
              </button>
            </div>

            {/* Filter Panel */}
            {showFilter && (
              <div className='filter-panel'>
                <div className='filter-header'>
                  <h3>{t('branches.filter')}</h3>
                  <button 
                    className='close-filter-btn'
                    onClick={() => setShowFilter(false)}
                  >
                    <XMarkIcon className='icon' />
                  </button>
                </div>
                
                <div className='filter-content'>
                  <div className='filter-group'>
                    <label>{t('branches.search')}</label>
                    <input
                      type='text'
                      placeholder={t('branches.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className='filter-input'
                    />
                  </div>

                  <div className='filter-group'>
                    <label>{t('categories.sortBy') || 'Sort By'}</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className='filter-select'
                    >
                      <option value='name'>{t('branches.sortByName')}</option>
                      <option value='display_order'>{t('branches.sortByOriginal')}</option>
                    </select>
                  </div>

                  <div className='filter-results'>
                    <span>{t('branches.results')}: {filteredBranches.length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Branches List */}
            <div className='branches-list'>
              <div className='row'>
                {filteredBranches.length > 0 ? (
                  filteredBranches.map((branch) => (
                    <div className='col-lg-6 col-md-6 col-6' key={branch.id}>
                      <Link to={`/branch/${branch.id}`}>
                        <div className='branch-item'>
                          <img 
                            src={branch.image} 
                            alt={branch.name} 
                            className='branch-image' 
                          />
                          <div className='branch-info'>
                            <h3 className='branch-name'>{branch.name}</h3>
                            <p className='branch-location'>{branch.location}</p>
                            {branch.phone && (
                              <p className='branch-phone'>{branch.phone}</p>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))
                ) : (
                  <div className='col-6'>
                    <div className='no-branches'>
                      <p>{t('branches.noBranches')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
        <FooterNote />
    </section>
  );
};

export default Branches;
